// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Where a board lands in the panel.
 *
 * Ported from `placed_board_polys` (`sigmaKi.py:293-315`), which itself mirrors
 * KiKit's `Panel.appendBoard`. The rule is easy to get subtly wrong, so stating
 * it plainly: the chosen **anchor of the source-area box** is placed at
 * `position`, and the board rotates about that same point.
 *
 * Rotation is positive counter-clockwise, as KiCad shows it. Board coordinates
 * have Y pointing down, so that is a negative rotation in ordinary maths —
 * which is exactly what `model/geometry.ts`'s `rotate()` already implements, and
 * why the Python has to pass `-deg` to shapely.
 */

import { type BBox, bbox } from '../model/bbox.ts';
import { type Point, rotatedBoxExtent, transformPoint } from '../model/geometry.ts';
import type { Polygon, Ring } from './outline.ts';
import { ANCHOR_FRACTION, type Anchor, type Vec2 } from './spec.ts';

export interface Placement {
  /** The anchor point of the source area, in the source board's coordinates. */
  anchor: Point;
  /** Where that anchor lands, in panel coordinates. */
  position: Point;
  /** Degrees, positive counter-clockwise on screen. */
  rotation: number;
}

export function placementFor(
  sourceBounds: BBox,
  position: Vec2,
  anchor: Anchor = 'center',
  rotation = 0,
): Placement {
  const [fx, fy] = ANCHOR_FRACTION[anchor];
  return {
    anchor: [
      sourceBounds.minX + (sourceBounds.maxX - sourceBounds.minX) * fx,
      sourceBounds.minY + (sourceBounds.maxY - sourceBounds.minY) * fy,
    ],
    position: [position[0], position[1]],
    rotation,
  };
}

export function applyPlacement(p: Placement, x: number, y: number): Point {
  return transformPoint(
    x - p.anchor[0],
    y - p.anchor[1],
    p.rotation,
    p.position[0],
    p.position[1],
  );
}

export function placeRing(p: Placement, ring: Ring): Ring {
  return ring.map(([x, y]) => applyPlacement(p, x, y));
}

export function placePolygon(p: Placement, poly: Polygon): Polygon {
  return { ext: placeRing(p, poly.ext), holes: poly.holes.map((h) => placeRing(p, h)) };
}

export function placePolygons(p: Placement, polys: Polygon[]): Polygon[] {
  return polys.map((poly) => placePolygon(p, poly));
}

/**
 * Where the source area itself ends up.
 *
 * Exact rather than a re-measure of the placed outline: the source area is the
 * box KiKit cuts from, and a board whose graphics sit inside it should still
 * report the box.
 */
export function placedBounds(p: Placement, sourceBounds: BBox): BBox {
  const local = bbox(
    sourceBounds.minX - p.anchor[0],
    sourceBounds.minY - p.anchor[1],
    sourceBounds.maxX - p.anchor[0],
    sourceBounds.maxY - p.anchor[1],
  );
  return rotatedBoxExtent(local, p.rotation, p.position[0], p.position[1]);
}
